import { Box, Button, Tab, Tabs } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import SettingsLayout from "screens/Settings/components/SettingsLayout";
import {
  debounceEventHandler,
  initialPageFilterState,
  isTruthy,
  openErrorNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SettingsSearchField from "screens/Settings/components/SettingsSearchField";
import notifiers from "global/constants/NotificationConstants";
import {
  doFetchAllTicketsByStatus,
  doFetchAllTicketsCountByStatus,
  doFetchAllTicketsMetrics,
} from "./TicketServices";
import CreateTicket from "./CreateTicket/CreateTicket";
import TicketsTable, { Ticket } from "./components/TicketsTable";
import {
  createTicketButtonSx,
  tabBarSx,
  tabCountSx,
  tabLabelSx,
  tabSx,
  tabsSx,
  ticketsCardSx,
} from "./SupportTickets.styles";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { pageFilters } from "models/interfaces";

/** Fallbacks for the optional page filters, mirroring the tickets URL query. */
const FIRST_PAGE = 1;
const DEFAULT_ROWS_PER_PAGE = 10;

/** Wires the tab strip to the list it swaps (WAI-ARIA tabs pattern). */
const TAB_PANEL_ID = "tickets-tabpanel";
const tabId = (key: string) => `tickets-tab-${key}`;

const Tickets = () => {
  useTitle(title.TICKETS);
  const history = useHistory();
  const getTab = new URLSearchParams(useLocation().search).get("tab");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeData, setActiveData] = useState<Ticket[]>([]);
  const [count, setCount] = useState<number>(0);
  const [addCreateTicketModal, setAddCreateTicketModal] =
    useState<boolean>(false);
  const [metrics, setMetrics] = useState<any>();

  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");

  const [ticketFilters, setTicketFilters] = useState<pageFilters>(
    initialPageFilterState(
      searchFromUrl ?? "",
      pageFromUrl,
      rowsPerPageFromUrl,
    ),
  );

  const tabConfig = [
    {
      label: "Open",
      value: "Open",
      key: "open",
      count: metrics && metrics["Open"],
    },
    {
      label: "In Process",
      value: "In Process",
      key: "in-process",
      count: metrics && metrics["InProcess"],
    },
    {
      label: "Complete",
      value: "Complete",
      key: "complete",
      count: metrics && metrics["Complete"],
    },
  ];

  const fetchTab = (
    selectedTab: string = "",
    searchBasedOn: "label" | "key",
  ) => {
    const newSelection = tabConfig.find(
      (tab) => tab[searchBasedOn] === selectedTab,
    );
    return newSelection;
  };

  const tabToSelect = fetchTab(getTab ?? "", "key");
  const [currentTab, setCurrentTab] = useState<any>(
    tabToSelect?.label ?? "Open",
  );
  const activeTabKey = fetchTab(currentTab, "label")?.key ?? "open";

  useEffect(() => {
    fetchMetrics();
  }, [currentTab]);

  useEffect(() => {
    const { page, rowsPerPage, searchText } = ticketFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`,
    );
    getTableData();
  }, [ticketFilters, currentTab]);

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      const metrics = await doFetchAllTicketsMetrics();
      const metricsMap = metrics.reduce((map: any, metric: any) => {
        map[metric.name] = metric.count;
        return map;
      }, {});
      setMetrics(metricsMap);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const getTableData = async () => {
    const { searchText, page, rowsPerPage } = ticketFilters;
    const tab = currentTab === "In Process" ? "InProcess" : currentTab;
    try {
      setIsLoading(true);
      const count = await doFetchAllTicketsCountByStatus(tab, searchText);
      const response = await doFetchAllTicketsByStatus(
        tab,
        rowsPerPage,
        page,
        searchText,
      );
      setActiveData(response ?? []);
      setCount(count);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      // The tabs used to be `disabled` while a fetch was in flight, which threw
      // keyboard focus back to <body> the moment a tab was activated (2.4.3 /
      // 2.4.7). Guarding here keeps the same "no switching mid-fetch" behaviour
      // while leaving the tablist focusable.
      if (isLoading) return;
      const selectedTab = fetchTab(value, "label");
      history.replace(`${viewpaths.ticketsViewPath}?tab=${selectedTab!.key}`);
      setCurrentTab(value);
      setTicketFilters((filters) => ({
        ...filters,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        page: FIRST_PAGE,
      }));
    },
    [isLoading],
  );

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setTicketFilters((filters) => ({
      ...filters,
      page: FIRST_PAGE,
      searchText,
    }));
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setTicketFilters((filters) => ({
      ...filters,
      page: newPage,
    }));
  };

  return (
    <SettingsLayout
      activeTab="Support Tickets"
      title="Support Tickets"
      headerAction={
        <>
          <SettingsSearchField
            id="tickets_search_field"
            placeholder="Search tickets"
            ariaLabel="Search tickets"
            inputRef={searchTerm}
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT,
            )}
          />
          <Button
            id="ticket_add_btn"
            variant="contained"
            disableElevation
            disabled={isLoading}
            onClick={() => setAddCreateTicketModal(true)}
            sx={createTicketButtonSx}
          >
            Create Ticket
          </Button>
        </>
      }
    >
      <Box sx={ticketsCardSx}>
        <Box sx={tabBarSx}>
          <Tabs
            value={currentTab}
            onChange={handleTabsChange}
            variant="scrollable"
            scrollButtons={false}
            aria-label="Ticket status"
            sx={tabsSx}
          >
            {tabConfig.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                id={tabId(tab.key)}
                aria-controls={TAB_PANEL_ID}
                sx={tabSx}
                label={
                  <Box component="span" sx={tabLabelSx}>
                    {tab.label}
                    {tab.count > 0 && (
                      <Box component="span" sx={tabCountSx}>
                        {tab.count}
                        {/* The bare count reads as a stray number, so screen
                            readers hear "Open 3 tickets". Kept inside the
                            visible text so the name still contains it (2.5.3). */}
                        <Box component="span" sx={visuallyHidden}>
                          {tab.count === 1 ? " ticket" : " tickets"}
                        </Box>
                      </Box>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        <Box
          role="tabpanel"
          id={TAB_PANEL_ID}
          aria-labelledby={tabId(activeTabKey)}
        >
          <TicketsTable
            tickets={activeData}
            loading={isLoading}
            emptyLabel={currentTab.toLowerCase()}
            // The detail route reads `tab` back through `fetchTab(…, "key")`, so
            // the link has to carry the key, not the label.
            activeTab={activeTabKey}
            page={ticketFilters.page ?? FIRST_PAGE}
            rowsPerPage={ticketFilters.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE}
            count={count}
            onPageChange={pageChangeHandler}
          />
        </Box>
      </Box>

      <CreateTicket
        openModel={addCreateTicketModal}
        setOpenModel={setAddCreateTicketModal}
        handleDialogClose={() => setAddCreateTicketModal(false)}
        getTableData={getTableData}
        fetchMetrics={fetchMetrics}
      />
    </SettingsLayout>
  );
};

export default Tickets;
